/**
 * Laminate / Highlighter / ACP / Wooden Flooring card body.
 *
 * Renders two flavours depending on the product's `codes_and_rates`:
 *   • "Select Code & Add" — when 2+ meaningful codes exist (opens the
 *     LaminateCodeSelector modal).
 *   • Direct "Add" — when only a single code (or duplicate/empty codes)
 *     exists, skipping the modal.
 *
 * Extracted from ProductsPage.js. Pure JSX. The
 * `codes.some(cr => cr.code.trim().toUpperCase() !== group.name.toUpperCase())`
 * check preserves the legacy behaviour where a code like "PLAIN GLOSS"
 * that only echoes the subcategory name is treated as no-code, so
 * staff aren't forced through a pointless "select the one code" modal.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Plus } from 'lucide-react';
import { formatINR } from '../../../lib/store';


export function LaminateBlock({ group, idx, onSelectCode, onDirectAdd }) {
  const codes = Array.isArray(group.codes_and_rates) ? group.codes_and_rates : [];
  const hasMeaningfulCodes =
    codes.length > 1 &&
    codes.some(cr => cr && cr.code && cr.code.trim() &&
      cr.code.trim().toUpperCase() !== (group.name || '').toUpperCase());

  if (hasMeaningfulCodes) {
    return (
      <div className="space-y-2 flex-1 flex flex-col">
        {/* Thickness + Sizes info chips */}
        <div className="flex flex-wrap gap-2 text-xs text-heda-sub">
          {group.thickness && (
            <span className="bg-heda-gold-tint text-heda-gold px-2 py-1 rounded-lg font-medium border border-heda-gold/30">
              {group.thickness}
            </span>
          )}
          {group.available_sizes && group.available_sizes.map((size, i) => (
            <span key={i} className="bg-heda-cream/60 px-2 py-1 rounded-lg">{size}</span>
          ))}
        </div>

        <p className="text-xs text-heda-mute">{codes.length} codes available</p>

        <div className="flex justify-between items-center">
          <span className="text-sm text-heda-sub">Price Range:</span>
          <span className="font-mono-data font-semibold text-heda-olive-dark">
            {group.min_rate === group.max_rate
              ? formatINR(group.min_rate)
              : `${formatINR(group.min_rate)} - ${formatINR(group.max_rate)}`}
          </span>
        </div>

        <div className="mt-auto pt-2">
          <Button
            size="sm"
            className="rounded-xl w-full bg-heda-ink hover:bg-black text-white"
            onClick={() => onSelectCode(group)}
            data-testid={`laminate-add-btn-${idx}`}
          >
            <Plus className="w-4 h-4 mr-1" /> Select Code &amp; Add
          </Button>
        </div>
      </div>
    );
  }

  // Direct add path — single meaningful code (or duplicate/empty codes)
  return (
    <div className="space-y-2 flex-1 flex flex-col">
      <div className="flex flex-wrap gap-2 text-xs text-heda-sub">
        {group.thickness && (
          <span className="bg-heda-gold-tint text-heda-gold px-2 py-1 rounded-lg font-medium border border-heda-gold/30">
            {group.thickness}
          </span>
        )}
        {group.available_sizes && group.available_sizes.slice(0, 4).map((size, i) => (
          <span key={i} className="bg-heda-cream/60 px-2 py-1 rounded-lg">{size}</span>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <span className="text-sm text-heda-sub">Rate:</span>
        <span className="font-mono-data font-semibold text-heda-olive-dark">
          {formatINR(group.min_rate)}/sheet
        </span>
      </div>

      <div className="mt-auto pt-2">
        <Button
          size="sm"
          className="rounded-xl w-full bg-heda-ink hover:bg-black text-white"
          onClick={() => onDirectAdd(group.variants[0])}
          data-testid={`laminate-direct-add-btn-${idx}`}
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}
